import { Params, Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { Roles } from '@models/roles.models';
import { companyContextGuard } from 'utils/company-context.guard';
import { dataImportDeactivateGuard } from 'utils/data-import-deactivate.guard';
import { PAGE_TITLE_SUFFIX, resolveTitleForParam } from 'utils/page-title-resolver';
import { RoleGuard } from 'utils/role.guard';

export enum APP_ROUTES {
  HOME = 'home',
  DASHBOARD = 'dashboard',
  LOGIN = 'login',
  LOGOUT = 'logout',
  ACCESS_DENIED = 'access-denied',
  MAINTENANCE_MODE = 'maintenance-mode',
  CONNECTION_PROBLEM = 'connection-problem',
  REQUESTS = 'requests',
  ITEMS = 'items',
  SELECT_COMPANY = 'select-company',
  CLASSIFICATION = 'classification',
  EXPEDITE = 'expedite',
  DISPUTE = 'dispute',
  APP = 'app',
  SEARCH = 'search',
  AUDIT_HISTORY = 'audit-history',
  CREATE = 'create',
  VALIDATE = 'validate',
  SUMMARY = 'summary',
  SUBMITTED = 'submitted',
  INFORMATION_REQUESTS = 'information-requests',
  LEGACY_REDIRECT = 'legacy-redirect',
  DATA_IMPORTS = 'data-imports',
  CBAM = 'cbam',
  OVERVIEW = 'overview',
  QUICK_CREATE = 'quick-create',
}

export const REQUESTS_ROLES = [
  Roles.CLIENT_USER,
  Roles.CLIENT_READER,
  Roles.CLIENT_MANAGER,
  Roles.CUSTOMS_BROKER,
  Roles.INTERNAL_REQUESTER,
  Roles.SUPPORT_ANALYST,
];

export const ITEMS_DIRECT_ROLES = [
  Roles.CLIENT_USER,
  Roles.CLIENT_READER,
  Roles.CLIENT_MANAGER,
  Roles.CUSTOMS_BROKER,
  Roles.INTERNAL_REQUESTER,
  Roles.SUPPORT_ANALYST,
];

export const DISPUTE_REQUESTS_ROLES = [
  Roles.CLIENT_MANAGER,
  Roles.DISPUTE_MANAGER,
  Roles.INTERNAL_REQUESTER,
  Roles.SUPPORT_ANALYST,
];

export const CREATE_REQUEST_ROLES = [
  Roles.CLIENT_USER,
  Roles.CLIENT_MANAGER,
  Roles.CUSTOMS_BROKER,
  Roles.INTERNAL_REQUESTER,
];

export const MANAGE_REQUEST_DOCUMENTS_ROLES = [
  Roles.CLIENT_USER,
  Roles.CLIENT_MANAGER,
  Roles.CUSTOMS_BROKER,
  Roles.INTERNAL_REQUESTER,
];

export const EXPEDITE_REQUESTS_ROLES = [Roles.CLIENT_MANAGER, Roles.SUPPORT_ANALYST, Roles.INTERNAL_REQUESTER];

export const EXPEDITE_REQUESTS_COMMENT_ROLES = [Roles.CLIENT_MANAGER, Roles.INTERNAL_REQUESTER];

export const CREATE_EXPEDITE_REQUESTS_ROLES = [Roles.CLIENT_MANAGER];

export const AUDIT_HISTORY_ROLES = [Roles.CLIENT_MANAGER, Roles.SUPPORT_ANALYST, Roles.INTERNAL_REQUESTER];

export const CREATE_DISPUTE_REQUEST_ROLES = [Roles.CLIENT_MANAGER, Roles.DISPUTE_MANAGER, Roles.INTERNAL_REQUESTER];

export const ADD_DISPUTE_ITEM_COMMENT_ROLES = [Roles.CLIENT_MANAGER, Roles.DISPUTE_MANAGER, Roles.INTERNAL_REQUESTER];

export const ADD_DISPUTE_ITEM_DOCUMENT_ROLES = [Roles.CLIENT_MANAGER, Roles.DISPUTE_MANAGER, Roles.INTERNAL_REQUESTER];

export const ADD_EXPEDITE_REQUEST_COMMENT_ROLES = EXPEDITE_REQUESTS_ROLES;

export const ADD_INFORMATION_REQUEST_COMMENT_ROLES = [
  Roles.TECHNICAL_ENGINEER,
  Roles.TECHNICAL_ENGINEER_MANAGER,
  Roles.INTERNAL_REQUESTER,
];

export const ADD_IMPORT_FILE_COMMENT_ROLES = [
  Roles.CBAM_MANAGER,
  Roles.CBAM_REPORTER,
  Roles.CBAM_CLIENT_MANAGER,
  Roles.CBAM_CLIENT_REPORTER,
];

export const DASHBOARD_ROLES = [
  Roles.GOVERNANCE_MANAGER_EXTERNAL,
  Roles.CBAM_ANALYST,
  Roles.INTERNAL_CBAM_ANALYST,
  Roles.TRADE_ANALYST,
  Roles.INTERNAL_REQUESTER,
  Roles.SUPPORT_ANALYST,
  Roles.CBAM_CLIENT_REPORTER,
  Roles.CBAM_CLIENT_MANAGER,
  Roles.CBAM_REPORTER,
  Roles.CBAM_MANAGER,
];
export const DASHBOARD_ACCESS = {
  [Roles.GOVERNANCE_MANAGER_EXTERNAL]: ['Classification'],
  [Roles.CBAM_ANALYST]: ['CBAM'],
  [Roles.INTERNAL_CBAM_ANALYST]: ['CBAM'],
  [Roles.TRADE_ANALYST]: ['Trade Analysis'],
  [Roles.INTERNAL_REQUESTER]: ['Classification'],
  [Roles.SUPPORT_ANALYST]: ['Classification', 'CBAM', 'Trade Analysis'],
};

export const INFORMATION_REQUESTS_ROLES = [
  Roles.TECHNICAL_ENGINEER_MANAGER,
  Roles.TECHNICAL_ENGINEER,
  Roles.INTERNAL_REQUESTER,
  Roles.SUPPORT_ANALYST,
];

export const INFORMATION_REQUESTS_EDIT_ROLES = [
  Roles.TECHNICAL_ENGINEER_MANAGER,
  Roles.TECHNICAL_ENGINEER,
  Roles.INTERNAL_REQUESTER,
];

export const INFORMATION_REQUESTS_REASSIGNMENT_ROLES = [Roles.TECHNICAL_ENGINEER_MANAGER, Roles.TECHNICAL_ENGINEER];

export const DATA_IMPORTS_ROLES = [
  Roles.CBAM_CLIENT_REPORTER,
  Roles.CBAM_CLIENT_MANAGER,
  Roles.CBAM_REPORTER,
  Roles.CBAM_MANAGER,
];

export const DATA_IMPORTS_OVERVIEW_ROLES = [
  Roles.CBAM_CLIENT_REPORTER,
  Roles.CBAM_CLIENT_MANAGER,
  Roles.CBAM_REPORTER,
  Roles.CBAM_MANAGER,
];

export const DATA_UPLOAD_ROLES = [
  Roles.CBAM_CLIENT_REPORTER,
  Roles.CBAM_CLIENT_MANAGER,
  Roles.CBAM_REPORTER,
  Roles.CBAM_MANAGER,
];

export const FILE_SUMMARY_REQUEST_APPROVAL_ROLES = [Roles.CBAM_REPORTER, Roles.CBAM_CLIENT_REPORTER];

export const FILE_SUMMARY_APPROVE_ROLES = [Roles.CBAM_MANAGER, Roles.CBAM_CLIENT_MANAGER];

export const FILE_SUMMARY_REJECT_ROLES = [
  Roles.CBAM_REPORTER,
  Roles.CBAM_MANAGER,
  Roles.CBAM_CLIENT_REPORTER,
  Roles.CBAM_CLIENT_MANAGER,
];

export const FILE_SUMMARY_RE_VALIDATE_ROLES = [
  Roles.CBAM_REPORTER,
  Roles.CBAM_MANAGER,
  Roles.CBAM_CLIENT_REPORTER,
  Roles.CBAM_CLIENT_MANAGER,
];

export const SEARCH_ROLES = [
  Roles.CLIENT_USER,
  Roles.CLIENT_READER,
  Roles.CLIENT_MANAGER,
  Roles.CUSTOMS_BROKER,
  Roles.INTERNAL_REQUESTER,
  Roles.SUPPORT_ANALYST,
];

export const FULL_DASHBOARD_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.DASHBOARD}`;
export const FULL_LOGIN_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.LOGIN}`;
export const FULL_LOGOUT_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.LOGOUT}`;
export const FULL_ACCESS_DENIED_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.ACCESS_DENIED}`;
export const FULL_MAINTENANCE_MODE_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.MAINTENANCE_MODE}`;
export const FULL_CONNECTION_PROBLEM_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.CONNECTION_PROBLEM}`;
export const FULL_CLASSIFICATION_REQUESTS_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.CLASSIFICATION}`;
export const FULL_EXPEDITE_REQUESTS_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.EXPEDITE}`;
export const FULL_CREATE_EXPEDITE_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.EXPEDITE}/${APP_ROUTES.CREATE}`;
export const FULL_DISPUTE_REQUESTS_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.DISPUTE}`;
export const FULL_CREATE_DISPUTE_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.DISPUTE}/${APP_ROUTES.CREATE}`;
export const FULL_INFORMATION_REQUESTS_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.INFORMATION_REQUESTS}`;
export const FULL_SELECT_COMPANY_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.SELECT_COMPANY}`;
export const FULL_HOME_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.HOME}`;
export const FULL_SEARCH_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.SEARCH}`;
export const FULL_AUDIT_HISTORY_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.AUDIT_HISTORY}`;
export const FULL_CREATE_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.CLASSIFICATION}/${APP_ROUTES.CREATE}`;
export const FULL_QUICK_CREATE_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.CLASSIFICATION}/${APP_ROUTES.QUICK_CREATE}`;
export const REQUEST_CREATION_SOURCE_QUERY_PARAM = 'source';
export const QUICK_CREATE_REQUEST_SOURCE = 'quick-create';
export const FULL_VALIDATE_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.CLASSIFICATION}/${APP_ROUTES.CREATE}/${APP_ROUTES.VALIDATE}`;
export const FULL_SUBMITTED_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.CLASSIFICATION}/${APP_ROUTES.CREATE}/${APP_ROUTES.SUBMITTED}`;
export const FULL_VALIDATE_DISPUTE_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.DISPUTE}/${APP_ROUTES.CREATE}/${APP_ROUTES.VALIDATE}`;
export const FULL_SUBMITTED_DISPUTE_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.DISPUTE}/${APP_ROUTES.CREATE}/${APP_ROUTES.SUBMITTED}`;
export const FULL_DATA_IMPORTS_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.CBAM}/${APP_ROUTES.DATA_IMPORTS}`;
export const FULL_DATA_UPLOAD_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.CBAM}/${APP_ROUTES.DATA_IMPORTS}/${APP_ROUTES.CREATE}`;
export const FULL_DATA_VALIDATE_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.CBAM}/${APP_ROUTES.DATA_IMPORTS}/${APP_ROUTES.CREATE}/${APP_ROUTES.VALIDATE}`;
export const FULL_DATA_SUMMARY_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.CBAM}/${APP_ROUTES.DATA_IMPORTS}/${APP_ROUTES.CREATE}/${APP_ROUTES.SUMMARY}`;
export const FULL_DATA_IMPORTS_OVERVIEW_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.CBAM}/${APP_ROUTES.OVERVIEW}`;

export const ROUTES = {
  FULL_DASHBOARD_ROUTE,
  FULL_LOGIN_ROUTE,
  FULL_LOGOUT_ROUTE,
  FULL_CLASSIFICATION_REQUESTS_ROUTE,
  FULL_EXPEDITE_REQUESTS_ROUTE,
  FULL_DISPUTE_REQUESTS_ROUTE,
  FULL_INFORMATION_REQUESTS_ROUTE,
  FULL_HOME_ROUTE,
  FULL_SEARCH_ROUTE,
  FULL_AUDIT_HISTORY_ROUTE,
  FULL_DATA_IMPORTS_ROUTE,
  FULL_DATA_IMPORTS_OVERVIEW_ROUTE,
} as const;

const LOGIN_ROUTES: Routes = [
  {
    path: APP_ROUTES.LOGIN,
    title: 'Login' + PAGE_TITLE_SUFFIX,
    loadComponent: () => import('./views/pages/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: APP_ROUTES.LOGOUT,
    title: 'Logout' + PAGE_TITLE_SUFFIX,
    loadComponent: () => import('./views/pages/logout/logout.component').then(m => m.LogoutComponent),
  },
  {
    path: APP_ROUTES.CONNECTION_PROBLEM,
    title: 'Connection Problem' + PAGE_TITLE_SUFFIX,
    loadComponent: () =>
      import('./views/pages/connection-problem/connection-problem.component').then(m => m.ConnectionProblemComponent),
  },
  {
    path: APP_ROUTES.ACCESS_DENIED,
    title: 'Access Denied' + PAGE_TITLE_SUFFIX,
    loadComponent: () =>
      import('./views/pages/access-denied/access-denied.component').then(m => m.AccessDeniedComponent),
  },
  {
    path: APP_ROUTES.MAINTENANCE_MODE,
    title: 'Maintenance Mode' + PAGE_TITLE_SUFFIX,
    loadComponent: () =>
      import('./views/pages/maintenance-mode/maintenance-mode.component').then(m => m.MaintenanceModeComponent),
  },
  {
    path: APP_ROUTES.LEGACY_REDIRECT,
    title: 'Legacy Redirect' + PAGE_TITLE_SUFFIX,
    data: { hideEYTCBackground: true },
    loadComponent: () =>
      import('./views/pages/legacy-redirect/legacy-redirect.component').then(m => m.LegacyRedirectComponent),
  },
] as const;

const HOME_ROUTES: Routes = [
  {
    path: APP_ROUTES.HOME,
    canActivate: [MsalGuard],
    data: { crumb: "What's New" },
    title: "What's New" + PAGE_TITLE_SUFFIX,
    loadComponent: () => import('./views/pages/news/news.component').then(m => m.NewsComponent),
  },
] as const;

const SEARCH_ROUTES: Routes = [
  {
    path: APP_ROUTES.SEARCH,
    canActivate: [MsalGuard, companyContextGuard, RoleGuard],
    data: {
      roles: SEARCH_ROLES,
      crumb: 'Product Search',
    },
    title: 'Product Search' + PAGE_TITLE_SUFFIX,
    loadComponent: () => import('./views/pages/search/search.component').then(m => m.SearchComponent),
  },
] as const;

const AUDIT_HISTORY_ROUTES: Routes = [
  {
    path: APP_ROUTES.AUDIT_HISTORY,
    canActivate: [MsalGuard, companyContextGuard, RoleGuard],
    data: {
      roles: AUDIT_HISTORY_ROLES,
      crumb: 'Audit History',
    },
    title: 'Audit History' + PAGE_TITLE_SUFFIX,
    loadComponent: () =>
      import('./views/pages/audit-history/audit-history.component').then(m => m.AuditHistoryComponent),
  },
] as const;

const USER_PROFILE_ROUTES: Routes = [
  {
    path: APP_ROUTES.SELECT_COMPANY,
    canActivate: [MsalGuard],
    title: 'Select Company' + PAGE_TITLE_SUFFIX,
    loadComponent: () =>
      import('./views/pages/select-company/select-company.component').then(m => m.SelectCompanyComponent),
  },
];

const ANONYMOUS_ROUTES: Routes = [
  {
    path: '',
    data: { hidePrivacyNoticeLink: true, allowLoader: false },
    loadComponent: () => import('./views/layout/anonymous/anonymous.component').then(m => m.AnonymousComponent),
    children: [...LOGIN_ROUTES, ...USER_PROFILE_ROUTES],
  },
] as const;

const DASHBOARD_ROUTES: Routes = [
  {
    path: APP_ROUTES.DASHBOARD,
    canActivate: [MsalGuard, companyContextGuard, RoleGuard],
    data: {
      roles: DASHBOARD_ROLES,
      crumb: 'Dashboard',
    },
    title: 'Dashboard' + PAGE_TITLE_SUFFIX,
    loadComponent: () => import('./views/pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
  },
] as const;

export const ITEMS_ROUTES: Routes = [
  {
    path: `:itemEntityId/:itemId`,
    data: { crumb: (params: Params) => `${params['itemId']}` },
    title: resolveTitleForParam('itemId'),
    loadComponent: () => import('./views/pages/item-details/item-details.component').then(m => m.ItemDetailsComponent),
  },
];

export const DISPUTE_ITEMS_ROUTES: Routes = [
  {
    path: `:disputeItemEntityId/:disputeItemId`,
    data: { crumb: (params: Params) => `${params['disputeItemId']}` },
    title: resolveTitleForParam('disputeItemId'),
    loadComponent: () =>
      import('./views/pages/dispute-item-details/dispute-item-details.component').then(
        m => m.DisputeItemDetailsComponent
      ),
  },
];

const REQUESTS_ROUTES: Routes = [
  {
    path: APP_ROUTES.REQUESTS,
    redirectTo: FULL_HOME_ROUTE,
    pathMatch: 'full',
  },
  {
    path: APP_ROUTES.REQUESTS,
    canActivate: [MsalGuard, companyContextGuard],
    children: [
      {
        path: APP_ROUTES.CLASSIFICATION,
        canActivate: [MsalGuard, companyContextGuard, RoleGuard],
        data: {
          crumb: 'Classification Requests',
          roles: REQUESTS_ROLES,
        },
        title: 'Classification Requests' + PAGE_TITLE_SUFFIX,
        children: [
          {
            path: ``,
            data: { crumb: undefined },
            loadComponent: () =>
              import('./views/pages/requests/classification/classification.component').then(
                m => m.ClassificationComponent
              ),
          },
          {
            path: APP_ROUTES.CREATE,
            canActivate: [RoleGuard],
            data: {
              crumb: 'Create Classification Request',
              roles: CREATE_REQUEST_ROLES,
            },
            title: 'Create Classification Request' + PAGE_TITLE_SUFFIX,
            children: [
              {
                path: '',
                data: { crumb: undefined },
                loadComponent: () =>
                  import('./views/pages/requests/classification/create-request/create-request.component').then(
                    m => m.CreateRequestComponent
                  ),
              },
              {
                path: 'validate/:requestValidationSubmissionId',
                data: { crumb: 'Data Validation' },
                title: 'Data Validation' + PAGE_TITLE_SUFFIX,
                loadComponent: () =>
                  import('./views/pages/requests/classification/validate-request/validate-request.component').then(
                    m => m.ValidateRequestComponent
                  ),
              },
              {
                path: 'submitted/:requestValidationSubmissionId/:requestEntityId/:requestId',
                data: { crumb: 'Submitted Successfully' },
                title: 'Submitted Successfully' + PAGE_TITLE_SUFFIX,
                loadComponent: () =>
                  import('./views/pages/requests/submitted-request/submitted-request.component').then(
                    m => m.SubmittedRequestComponent
                  ),
              },
            ],
          },
          {
            path: `quick-create`,
            canActivate: [RoleGuard],
            data: {
              crumb: 'Products to Classify',
              roles: CREATE_REQUEST_ROLES,
            },
            title: 'Products to Classify' + PAGE_TITLE_SUFFIX,
            loadComponent: () =>
              import('./views/pages/requests/classification/quick-create/quick-create.component').then(
                m => m.QuickCreateComponent
              ),
          },
          {
            path: `:requestEntityId/:requestId`,
            data: { crumb: (params: Params) => `${params['requestId']}` },
            title: resolveTitleForParam('requestId'),
            children: [
              {
                path: '',
                data: { crumb: undefined },
                loadComponent: () =>
                  import('./views/pages/request-details/request-details.component').then(
                    m => m.RequestDetailsComponent
                  ),
              },

              ...ITEMS_ROUTES,
            ],
          },
        ],
      },

      {
        path: APP_ROUTES.EXPEDITE,
        canActivate: [MsalGuard, companyContextGuard, RoleGuard],
        data: { crumb: 'Expedite Requests', roles: EXPEDITE_REQUESTS_ROLES },
        title: 'Expedite Requests' + PAGE_TITLE_SUFFIX,
        children: [
          {
            path: ``,
            data: { crumb: undefined },
            loadComponent: () =>
              import('./views/pages/expedite-requests/expedite-requests.component').then(
                m => m.ExpediteRequestsComponent
              ),
          },

          {
            path: `:expediteRequestEntityId/:expediteRequestId`,
            data: { crumb: (params: Params) => `${params['expediteRequestId']}` },
            title: resolveTitleForParam('expediteRequestId'),
            children: [
              {
                path: '',
                data: { crumb: undefined },
                loadComponent: () =>
                  import('./views/pages/expedite-request-details/expedite-request-details.component').then(
                    m => m.ExpediteRequestDetailsComponent
                  ),
              },
              {
                path: 'items',
                data: { crumb: undefined },
                children: [...ITEMS_ROUTES],
              },
            ],
          },
        ],
      },

      {
        path: APP_ROUTES.DISPUTE,
        canActivate: [MsalGuard, companyContextGuard, RoleGuard],
        data: { crumb: 'Dispute Requests', roles: DISPUTE_REQUESTS_ROLES },
        title: 'Dispute Requests' + PAGE_TITLE_SUFFIX,
        children: [
          {
            path: ``,
            data: { crumb: undefined },
            loadComponent: () =>
              import('./views/pages/dispute-requests/dispute-requests.component').then(m => m.DisputeRequestsComponent),
          },
          {
            path: APP_ROUTES.CREATE,
            canActivate: [RoleGuard],
            data: {
              crumb: 'Create Dispute Request',
              roles: CREATE_DISPUTE_REQUEST_ROLES,
            },
            title: 'Create Dispute Request' + PAGE_TITLE_SUFFIX,
            children: [
              {
                path: '',
                data: { crumb: undefined },
                loadComponent: () =>
                  import('./views/pages/dispute-requests/create-dispute-request/create-dispute-request.component').then(
                    m => m.CreateDisputeRequestComponent
                  ),
              },
              {
                path: 'validate/:disputeRequestValidationSubmissionId',
                data: { crumb: 'Data Validation' },
                title: 'Data Validation' + PAGE_TITLE_SUFFIX,
                loadComponent: () =>
                  import(
                    './views/pages/dispute-requests/validate-dispute-request/validate-dispute-request.component'
                  ).then(m => m.ValidateDisputeRequestComponent),
              },
              {
                path: 'submitted/:disputeRequestValidationSubmissionId/:disputeRequestEntityId/:disputeRequestId',
                data: { crumb: 'Submitted Successfully' },
                title: 'Submitted Successfully' + PAGE_TITLE_SUFFIX,
                loadComponent: () =>
                  import('./views/pages/dispute-requests/submit-dispute-request/submit-dispute-request.component').then(
                    m => m.SubmitDisputeRequestComponent
                  ),
              },
            ],
          },
          {
            path: `:disputeRequestEntityId/:disputeRequestId`,
            data: { crumb: (params: Params) => `${params['disputeRequestId']}` },
            title: resolveTitleForParam('disputeRequestId'),
            children: [
              {
                path: '',
                data: { crumb: undefined },
                loadComponent: () =>
                  import('./views/pages/dispute-request-details/dispute-request-details.component').then(
                    m => m.DisputeRequestDetailsComponent
                  ),
              },

              {
                path: 'items',
                data: { crumb: undefined },
                children: [...ITEMS_ROUTES],
              },
              ...DISPUTE_ITEMS_ROUTES,
            ],
          },
        ],
      },
    ],
  },
  { path: '', redirectTo: FULL_HOME_ROUTE, pathMatch: 'full' },
] as const;

export const ITEMS_DIRECT_ROUTES: Routes = [
  {
    path: 'items',
    canActivate: [MsalGuard, companyContextGuard, RoleGuard],
    data: {
      roles: ITEMS_DIRECT_ROLES,
    },
    children: [...ITEMS_ROUTES],
  },
];

export const INFORMATION_REQUESTS_ROUTES: Routes = [
  {
    path: APP_ROUTES.INFORMATION_REQUESTS,
    canActivate: [MsalGuard, companyContextGuard, RoleGuard],
    data: {
      crumb: 'Information Requests',
      roles: INFORMATION_REQUESTS_ROLES,
    },
    title: 'Information Requests' + PAGE_TITLE_SUFFIX,
    children: [
      {
        path: '',
        data: { crumb: undefined },
        loadComponent: () =>
          import('./views/pages/information-requests/information-requests.component').then(
            m => m.InformationRequestsComponent
          ),
      },
      {
        path: ':informationRequestEntityId/:informationRequestId',
        data: { crumb: (params: Params) => `${params['informationRequestId']}` },
        title: resolveTitleForParam('informationRequestId'),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./views/pages/information-request-details/information-request-details.component').then(
                m => m.InformationRequestDetailsComponent
              ),
          },
          ...ITEMS_ROUTES,
        ],
      },
      {
        path: 'items',
        data: { crumb: undefined },
        children: [...ITEMS_ROUTES],
      },
    ],
  },
];

const DATA_IMPORTS_ROUTES: Routes = [
  {
    path: APP_ROUTES.DATA_IMPORTS,
    canActivate: [MsalGuard, companyContextGuard, RoleGuard],
    data: {
      roles: DATA_IMPORTS_ROLES,
      crumb: 'Data Imports',
    },
    title: 'Data Imports' + PAGE_TITLE_SUFFIX,
    children: [
      {
        path: ``,
        data: { crumb: undefined },
        loadComponent: () =>
          import('./views/pages/data-imports/data-imports.component').then(m => m.DataImportsComponent),
      },
      {
        path: `${APP_ROUTES.SUMMARY}/:importFileId`,
        data: { crumb: 'File Summary' },
        title: 'File Summary' + PAGE_TITLE_SUFFIX,
        loadComponent: () =>
          import('./views/pages/data-imports/file-summary/file-summary.component').then(m => m.FileSummaryComponent),
      },
      {
        path: APP_ROUTES.CREATE,
        canActivate: [RoleGuard],
        data: {
          crumb: 'Upload Data',
          roles: DATA_UPLOAD_ROLES,
        },
        title: 'Upload Data' + PAGE_TITLE_SUFFIX,
        children: [
          {
            path: '',
            data: { crumb: undefined },
            canDeactivate: [dataImportDeactivateGuard],
            loadComponent: () =>
              import('./views/pages/data-imports/upload-data/upload-data.component').then(m => m.UploadDataComponent),
          },
          {
            path: `${APP_ROUTES.VALIDATE}/:importFileId`,
            data: { crumb: 'Data Validation' },
            title: 'Data Validation' + PAGE_TITLE_SUFFIX,
            canDeactivate: [dataImportDeactivateGuard],
            loadComponent: () =>
              import('./views/pages/data-imports/validate-data/validate-data.component').then(
                m => m.ValidateDataComponent
              ),
          },
          {
            path: `${APP_ROUTES.SUMMARY}/:importFileId`,
            data: { crumb: 'File Summary' },
            title: 'File Summary' + PAGE_TITLE_SUFFIX,
            loadComponent: () =>
              import('./views/pages/data-imports/file-summary/file-summary.component').then(
                m => m.FileSummaryComponent
              ),
          },
        ],
      },
    ],
  },
] as const;

const DATA_IMPORTS_OVERVIEW_ROUTES: Routes = [
  {
    path: APP_ROUTES.OVERVIEW,
    canActivate: [MsalGuard, companyContextGuard, RoleGuard],
    data: {
      roles: DATA_IMPORTS_OVERVIEW_ROLES,
      crumb: 'Overview',
    },
    title: 'Overview' + PAGE_TITLE_SUFFIX,
    children: [
      {
        path: '',
        data: { crumb: undefined },
        loadComponent: () =>
          import('./views/pages/data-imports-overview/data-imports-overview.component').then(
            m => m.DataImportsOverviewComponent
          ),
      },
      {
        path: ':legalEntityId/:legalEntityName',
        canActivate: [RoleGuard],
        data: {
          roles: DATA_IMPORTS_OVERVIEW_ROLES,
          crumb: (params: Params) => `${params['legalEntityName']}`,
        },
        title: resolveTitleForParam('legalEntityName'),
        children: [
          {
            path: '',
            data: { crumb: undefined },
            loadComponent: () =>
              import('./views/pages/legal-entity-periods-overview/legal-entity-periods-overview.component').then(
                m => m.LegalEntityPeriodsOverviewComponent
              ),
          },
          {
            path: ':periodDate/:period',
            canActivate: [RoleGuard],
            data: {
              roles: DATA_IMPORTS_OVERVIEW_ROLES,
              crumb: (params: Params) => `${params['period']}`,
            },
            title: resolveTitleForParam('period'),
            children: [
              {
                path: '',
                data: { crumb: undefined },
                loadComponent: () =>
                  import('./views/pages/period-files/period-files.component').then(m => m.PeriodFilesComponent),
              },
            ],
          },
        ],
      },
    ],
  },
] as const;

const CBAM_ROUTES: Routes = [
  {
    path: APP_ROUTES.CBAM,
    canActivate: [MsalGuard, companyContextGuard],
    children: [...DATA_IMPORTS_ROUTES, ...DATA_IMPORTS_OVERVIEW_ROUTES],
  },
] as const;

export const MAIN_ROUTES: Routes = [
  {
    path: '',
    canActivate: [MsalGuard, companyContextGuard],
    loadComponent: () => import('./views/layout/main/main.component').then(m => m.MainComponent),
    data: { crumb: 'Home', allowLoader: true },
    children: [
      ...DASHBOARD_ROUTES,
      ...REQUESTS_ROUTES,
      ...INFORMATION_REQUESTS_ROUTES,
      ...HOME_ROUTES,
      ...SEARCH_ROUTES,
      ...AUDIT_HISTORY_ROUTES,
      ...CBAM_ROUTES,
      ...ITEMS_DIRECT_ROUTES,
    ],
  },
] as const;

export const routes: Routes = [
  {
    path: APP_ROUTES.APP,
    children: [
      { path: '', redirectTo: APP_ROUTES.HOME, pathMatch: 'full' },
      ...ANONYMOUS_ROUTES,
      ...MAIN_ROUTES,
      { path: '**', redirectTo: APP_ROUTES.HOME, pathMatch: 'full' },
    ],
  },
  { path: '', redirectTo: FULL_HOME_ROUTE, pathMatch: 'full' },
  { path: '**', redirectTo: FULL_HOME_ROUTE, pathMatch: 'full' },
] as const;
